import React, { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import "./Power.css";
import { ToastContainer, toast } from "react-toastify";
import { MdDashboard } from "react-icons/md";
import { FaTasks, FaCloudUploadAlt, FaSignOutAlt, FaFileUpload } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import Source from './comps/SourceIMG.png';
import { FaRegFileAlt } from "react-icons/fa";
import { LiaSwatchbookSolid } from "react-icons/lia";
import Play from "./Playbook.jsx";
import Playbook1 from "./comps/Playbook1.jsx";
import ChoosePlay from './comps/Chooseplay.jsx';

const Dashboard = () => {
  const [report, setReport] = useState(null);
  const [embedConfig, setEmbedConfig] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState("dashboard");
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [datetimeFolder, setDatetimeFolder] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const history = useHistory();
  const embedUrl = process.env.REACT_APP_POWERBI_EMBED_URL;
  const reportId = process.env.REACT_APP_POWERBI_REPORT_ID;
  const usermail = sessionStorage.getItem("usermail");
  const [playbookStep, setPlaybookStep] = useState(1);

  useEffect(() => {
    const role = sessionStorage.getItem("userRole");
    setIsAdmin(role === "Admin");
    const fetchEmbedToken = async () => {
      try {
        const response = await toast.promise(
          axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/ocr/powerbi/`),
          {
            pending: "Fetching Power BI token...",
            success: "Token fetched successfully",
            error: "Failed to fetch token",
          }
        );
        const { embedToken } = response.data;
        setEmbedConfig({
          type: "report",
          embedUrl,
          id: reportId,
          accessToken: embedToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: { expanded: false, visible: false },
              pageNavigation: { visible: true },
            },

            background: models.BackgroundType.Transparent,
            navContentPaneEnabled: false,
            layoutType: models.LayoutType.Custom,
          },
        });
      } catch (error) {
        console.error("Failed to fetch Power BI token", error);
      }
    };
    fetchEmbedToken();
    const interval = setInterval(fetchEmbedToken, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let interval;
    if (datetimeFolder && usermail && isTracking) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BE_MAIN_URL}/ocr/uploadstatus/`, {
            params: {
              email: usermail,
              datetime_folder: datetimeFolder,
            },
          });
          setUploadStatus(response.data);
          if (response.data.is_done) {
            clearInterval(interval);
            setIsTracking(false);
          }
        } catch (error) {
          console.error("Polling error:", error);
          clearInterval(interval);
          setIsTracking(false);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [datetimeFolder, usermail, isTracking]);

  const handleUpload = async () => {
    if (!usermail || files.length === 0) {
      toast.error("No email or no files selected");
      return;
    }
    const maxRetries = 3;
    let uploadedFiles = 0;
    let totalFiles = files.length;
    let lastDatetimeFolder = null;
    const progressMap = new Map();
    setUploadProgress(0);
    setIsTracking(true);

    const uploadSingleFileWithRetry = async (file, retries = 0) => {
      const formData = new FormData();
      formData.append("email", usermail);
      formData.append("files", file);
      try {
        const res = await axios.post(`${process.env.REACT_APP_BE_MAIN_URL}/ocr/upload/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            progressMap.set(file.name, percent);
            const totalProgress = Array.from(progressMap.values()).reduce((a, b) => a + b, 0);
            const avgProgress = totalProgress / totalFiles;
            setUploadProgress(Math.min(Math.round(avgProgress), 95));
          },
        });
        if (res.data.datetime_folder) {
          lastDatetimeFolder = res.data.datetime_folder;
        }
        uploadedFiles += 1;
        progressMap.set(file.name, 100);
      } catch (error) {
        if (retries < maxRetries) {
          console.warn(`Retrying ${file.name} (attempt ${retries + 1})...`);
          await uploadSingleFileWithRetry(file, retries + 1);
        } else {
          toast.error(`Upload failed for ${file.name} after ${maxRetries} attempts.`);
          progressMap.set(file.name, 0);
        }
      }
    };
    for (const file of files) {
      await uploadSingleFileWithRetry(file);
    }
    if (uploadedFiles === totalFiles) {
      setUploadProgress(100);
      toast.success(" Files uploading!");
      setFiles([]);
      setDatetimeFolder(lastDatetimeFolder);
    } else {
      toast.error("Some files failed even after retrying.");
    }
  };

  const resetUploadState = () => {
    setFiles([]);
    setDatetimeFolder(null);
    setUploadStatus(null);
    setUploadProgress(0);
    setIsTracking(false);
    setCurrentView("dashboard");
  };

  return (
    <div className="w-full h-screen bg-gray-100 flex relative">
      <ToastContainer />
      <aside className="w-56 bg-gradient-to-r from-[#0c8c8f] via-[#155e63] to-[#0e4749] text-white p-6 flex flex-col justify-between shadow-lg">
        <div className="space-y-8">
          <img className="header-logo" src={Source} alt="Logo" style={{ height: "auto", width: "350px" }} />
          <div className="space-y-2">
            {isAdmin && (
              <>
                <button className="inline-flex w-56 items-center px-6 py-1 text-[16px] font-semibold text-white hover:text-orange-400 transition duration-300"
                  onClick={() => setCurrentView("dashboard")}
                >
                  <MdDashboard size={28} className="mr-3" /> Dashboard
                </button>
                <button className="inline-flex items-center px-6 py-[2px] w-56 text-[16px] font-semibold text-white hover:text-orange-400 transition duration-300"
                  onClick={() => history.push("/task")}
                >
                  <FaTasks size={28} className="mr-3" /> Task
                </button>
                <button className="inline-flex items-center px-6 py-[2px] w-56 text-[16px] font-semibold text-white hover:text-orange-400 transition duration-300"
                  onClick={() => setCurrentView("upload")}
                >
                  <FaCloudUploadAlt size={28} className="mr-3" /> Upload File
                </button>
                {/* <button
                  className="inline-flex items-center px-6 py-[2px] w-56 text-[16px] font-semibold text-white hover:text-orange-400 transition duration-300"
                  onClick={() => setCurrentView("playbook")}
                >
                  <LiaSwatchbookSolid size={28} className="mr-3" /> Play Book
                </button> */}

              <button className="inline-flex items-center px-6 py-[2px] w-56 text-[16px] font-semibold text-white hover:text-orange-400 transition duration-300"
                  onClick={() => history.push("/Playbook")}
                >
                  <LiaSwatchbookSolid size={28} className="mr-3" /> Play Book
                </button>

                
              </>
            )}
          </div>
        </div>
        <button
          className="flex items-center w-48 px-6 py-3 hover:text-red-400 text-xl font-semibold text-white rounded-xl transition duration-300"
          onClick={() => history.push("/login")}
        >
          <FaSignOutAlt size={24} className="mr-3" /> Logout
        </button>
      </aside>

      <main className="flex-1 flex flex-col h-screen w-full p-6 relative">
        {currentView === "dashboard" && (
          <div className="bg-white flex-1 rounded-lg shadow-md flex relative">
            {isLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-80 z-10 space-y-4">
                <div className="flex space-x-1">
                  <div className="w-1.5 h-5 bg-yellow-500 animate-[grow_1s_infinite]"></div>
                  <div className="w-1.5 h-5 bg-yellow-500 animate-[grow_1s_infinite_.2s]"></div>
                  <div className="w-1.5 h-5 bg-yellow-500 animate-[grow_1s_infinite_.4s]"></div>
                </div>
                <div className="text-lg font-semibold text-gray-700">Loading Report...</div>
              </div>
            )}
            {embedConfig && (
              <PowerBIEmbed
                embedConfig={embedConfig}
                eventHandlers={new Map([
                  ["loaded", () => console.log("Report loaded")],
                  ["rendered", () => setIsLoading(false)],
                  ["error", (event) => console.error("Power BI error", event.detail)],
                ])}
                cssClassName="Embed-container"
                getEmbeddedComponent={async (embeddedReport) => {
                  setReport(embeddedReport);
                  try {
                    const pages = await embeddedReport.getPages();
                    console.log("Pages:", pages.map((p) => p.displayName));
                  } catch (err) {
                    console.error("Error getting pages", err);
                  }
                }}
              />
            )}
          </div>
        )}

        {currentView === "upload" && (
          <div
            className="fixed inset-0 z-50 bg-white bg-opacity-80 backdrop-blur-md flex items-center justify-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files);
              setFiles((prev) => [...prev, ...droppedFiles]);
            }}
          >
            <div className="bg-white ml-56 justify-center rounded-lg shadow-lg p-6 w-[900px] max-w-xl relative">
              <h2 className="text-2xl font-bold text-center mb-4 text-teal-700">Upload Your Files</h2>
              <div className="border-2 border-dashed border-gray-300 p-8 text-center">
                <div className="text-black flex justify-center">
                  <FaFileUpload size={70} color="#0095FF" />
                </div>
                <p className="text-gray-700">Drag Files to upload</p>
                <div className="mt-4 flex justify-center space-x-4">
                  <input
                    type="file"
                    id="fileInput"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files);
                      setFiles((prev) => [...prev, ...selected]);
                    }}
                    disabled={isTracking || (uploadStatus && uploadStatus.is_done === false)}
                  />
                  <label
                    htmlFor="fileInput"
                    className={`bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded cursor-pointer ${isTracking ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    Choose File
                  </label>
                  <button className="bg-white border border-teal-600 text-teal-600 px-4 py-2 rounded">Google Drive</button>
                </div>
              </div>

              <div className="mt-6 max-h-40 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="mb-2 bg-gray-100 p-2 rounded shadow flex justify-between items-center">
                    <p className="text-sm text-black font-medium truncate">{file.name}</p>
                    <button
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                      className="text-red-500 font-bold text-lg px-2"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {uploadStatus && (
                <div className="bg-white p-4 mt-4 rounded shadow-md border border-gray-200">
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Status: {uploadStatus.is_done ? "✅ Done" : "⏳In Progress"}</span>
                      <span>{uploadStatus.completed}/{uploadStatus.total_files} Completed</span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-teal-600 text-lg"><FaRegFileAlt size={20} /></span>
                      <div className="w-full h-2 bg-teal-100 rounded overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-teal-500 via-teal-400 to-teal-600 animate-pulse transition-all duration-500 ease-in-out"
                          style={{
                            width: `${Math.round((uploadStatus.completed / uploadStatus.total_files) * 100)}%`,
                          }}
                        ></div>
                      </div>

                      <span className="text-sm text-gray-700 font-medium min-w-[35px] text-right">
                        {Math.round((uploadStatus.completed / uploadStatus.total_files) * 100)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-6 text-xs font-medium text-white">
                    <span className="bg-white border border-teal-600 text-teal-600 px-2 py-1 rounded"> Completed: {uploadStatus.completed}</span>
                    <span className="bg-white border border-blue-600 text-blue-600 px-2 py-1 rounded"> Uploading: {uploadStatus.uploading}</span>
                    <span className="bg-white border border-orange-400 text-orange-400 px-2 py-1 rounded"> Pending: {uploadStatus.pending}</span>
                    <span className="bg-white border border-red-600 text-red-500 px-2 py-1 rounded"> Failed: {uploadStatus.failed}</span>
                    <span className="bg-white border border-gray-600 text-gray-600 px-2 py-1 rounded"> Total: {uploadStatus.total_files}</span>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-center gap-14">
                <button
                  className="bg-red-600 hover:bg-red-700 px-8 py-2 rounded text-white"
                  onClick={() => {
                    if (uploadStatus && uploadStatus.is_done) {
                      resetUploadState();
                    } else {
                      setCurrentView("dashboard");
                    }
                  }}
                >
                  Close
                </button>
                <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-2 rounded disabled:opacity-50"
                  onClick={handleUpload}
                  disabled={isTracking || (uploadStatus && uploadStatus.is_done)}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}

        
        {/* {currentView === "playbook" && (
          <>
            {playbookStep === 1 && <Playbook onNext={() => setPlaybookStep(2)} />}
            {playbookStep === 2 && <ChoosePlay onBack={() => setPlaybookStep(1)} />}
          </>
        )} */}
      </main>
    </div>
  );
};

export default Dashboard;



